// La vista "Subí tu propio fondo": el alta de un fondo del cliente SIN salir del panel
// donde se está eligiendo (ADR-2026-08-25-el-fondo-se-sube-en-el-panel).
//
// Nació adentro de `FondoGrilla` (el panel de fondos de Modelo y del Catálogo) y se sacó a
// un componente propio cuando "Ambiente completo" necesitó la MISMA subida en su panel de
// Ambiente (ADR-2026-09-28-ambiente-completo-fondo-propio): el archivo va al mismo bucket
// y a la misma tabla `fondos_personalizados`, así que lo que se sube desde cualquiera de
// los dos modos queda en "Mis fondos" para todos. Es una sola vista para que el texto, los
// consejos y el manejo de errores no se bifurquen.
//
// El componente sube (`uploadFondo`) y avisa con el fondo recién guardado; qué hacer con
// él —dejarlo elegido, recargar la lista, volver a qué vista— lo decide el padre.

import React from 'react';
import { Check, ChevronLeft, Upload } from 'lucide-react';
import CachedImg from '../../../components/CachedImg';
import { uploadFondo, type FondoGuardado } from '../fondosApi';

export interface SubirFondoViewProps {
  /**
   * La empresa dueña del fondo que se sube. Sin ella no hay dónde guardarlo: la vista
   * avisa y no intenta.
   */
  companyId: string | null;
  /** "Volver": se abandona esta forma de elegir, no la elección. */
  onVolver: () => void;
  /** Se llama al terminar el alta, con el fondo nuevo (ya con su miniatura firmada). */
  onSubido: (fondo: FondoGuardado) => void;
}

export const SubirFondoView: React.FC<SubirFondoViewProps> = ({
  companyId,
  onVolver,
  onSubido,
}) => {
  const [subiendo, setSubiendo] = React.useState(false);
  const [errorSubida, setErrorSubida] = React.useState<string | null>(null);
  const fileRef = React.useRef<HTMLInputElement>(null);

  /**
   * Sube el archivo y avisa con el fondo nuevo.
   *
   * Subir un fondo es pedirlo para esta imagen: dejarlo sólo guardado obligaría a
   * buscarlo después en "Mis fondos". Ver ADR-2026-08-25-el-fondo-se-sube-en-el-panel.
   */
  const subirArchivo = async (file: File) => {
    if (!companyId) {
      setErrorSubida('No pudimos identificar la empresa.');
      return;
    }
    setSubiendo(true);
    setErrorSubida(null);
    try {
      const saved = await uploadFondo({ companyId, file });
      onSubido(saved);
    } catch (err) {
      console.error('[SubirFondoView] uploadFondo error', err);
      setErrorSubida(
        err instanceof Error
          ? err.message
          : 'No pudimos subir el fondo. Probá de nuevo.',
      );
    } finally {
      setSubiendo(false);
    }
  };

  // Alta de un fondo propio SIN salir del panel, igual que "Subir pose" en el picker
  // de poses (ADR-2026-08-25-el-fondo-se-sube-en-el-panel).
  return (
    <div className="flex flex-col gap-4 min-h-full animate-mode-swap">
      <button
        type="button"
        onClick={onVolver}
        className="inline-flex items-center gap-1 text-brand-primary text-[13px] hover:opacity-75 transition-opacity self-start"
      >
        <ChevronLeft className="w-3.5 h-3.5" />
        Volver
      </button>
      <div>
        <p className="text-[16px] font-semibold text-brand-black/90">
          Subí tu propio fondo
        </p>
        <p className="text-[13.5px] text-[#3C3C43]/65 mt-1 leading-relaxed">
          La foto que subas se guarda entre tus fondos y queda elegida para esta
          imagen. Después la reusás desde "Mis fondos" cuando quieras.
        </p>
      </div>
      <ul className="text-[13px] text-[#3C3C43]/65 list-disc pl-4 space-y-1.5 leading-snug">
        <li>Una foto del lugar SIN personas ni productos funciona mejor.</li>
        <li>
          Cuanto más grande la imagen, mejor: se usa como escena completa.
        </li>
      </ul>
      {errorSubida && (
        <p className="text-[13px] text-red-600">{errorSubida}</p>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Se limpia SIEMPRE: sin esto, subir el mismo archivo dos veces seguidas no
          // vuelve a disparar el `change`.
          e.target.value = '';
          if (file) void subirArchivo(file);
        }}
      />
      {/* La ZONA clickeable, como en el picker de fondos de siempre, en vez de un botón
          al pie: el recuadro punteado dice dónde va el archivo, ocupa el lugar que
          después va a ocupar la imagen y es un blanco mucho más grande para el click.
          Ver ADR-2026-08-25-el-fondo-se-sube-en-el-panel. */}
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={subiendo}
        className="w-full inline-flex flex-col items-center justify-center gap-2 py-10 rounded-2xl border-2 border-dashed border-neutral-300 hover:border-brand-primary/40 bg-white text-[#3C3C43]/55 hover:text-brand-primary transition-colors disabled:opacity-60 disabled:cursor-wait"
      >
        {subiendo ? (
          <>
            <span className="w-6 h-6 rounded-full border-2 border-brand-primary/30 border-t-brand-primary animate-spin" />
            <span className="text-[14px] font-medium">Subiendo el fondo…</span>
          </>
        ) : (
          <>
            <Upload className="w-6 h-6" />
            <span className="text-[14px] font-medium">Subir imagen</span>
          </>
        )}
      </button>
    </div>
  );
};

/** Tarjeta de un fondo con foto: la de "Mis fondos" y las carpetas de la biblioteca. */
export const CardFondo: React.FC<{
  nombre: string;
  imageUrl: string;
  elegido: boolean;
  onClick: () => void;
}> = ({ nombre, imageUrl, elegido, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`relative h-[var(--alto-fila,150px)] w-full rounded-2xl overflow-hidden bg-neutral-100 border-2 transition-all active:scale-[0.97] ${
      elegido ? 'border-brand-primary' : 'border-neutral-200'
    }`}
  >
    <CachedImg
      src={imageUrl}
      alt={nombre}
      className="absolute inset-0 w-full h-full object-cover"
    />
    {/* El nombre sobre la foto, con degradado: en una grilla de fotos, un pie de texto
        aparte gasta alto y separa el nombre de lo que nombra. */}
    <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 pt-6 pb-1.5 text-left">
      <span className="block text-[12px] font-medium text-white truncate">
        {nombre}
      </span>
    </span>
    {elegido && (
      <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-primary text-white shadow">
        <Check className="w-3.5 h-3.5" strokeWidth={3} />
      </span>
    )}
  </button>
);
